import axios, { AxiosError, type AxiosInstance } from 'axios';
import type { ApiEnvelope, ApiError, PageMeta } from './types';

const baseURL = import.meta.env.VITE_API_URL || '/api';

let accessToken: string | null = null;
let onAuthLost: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}
export function setAuthLostHandler(fn: () => void) {
  onAuthLost = fn;
}

export const http: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 30_000,
});

http.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

let refreshing: Promise<string | null> | null = null;

async function refresh(): Promise<string | null> {
  try {
    const res = await axios.post<ApiEnvelope<{ accessToken: string }>>(
      `${baseURL}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    const token = res.data.data.accessToken;
    accessToken = token;
    return token;
  } catch {
    return null;
  }
}

http.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<ApiError>) => {
    const original = error.config as (typeof error.config & { _retry?: boolean }) | undefined;
    const status = error.response?.status;
    const isAuthRoute = original?.url?.includes('/auth/');

    if (status === 401 && original && !original._retry && !isAuthRoute) {
      original._retry = true;
      refreshing ??= refresh().finally(() => {
        refreshing = null;
      });
      const token = await refreshing;
      if (token) {
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${token}`;
        return http(original);
      }
      onAuthLost?.();
    }
    return Promise.reject(error);
  },
);

export class ApiRequestError extends Error {
  code: string;
  status?: number;
  details?: unknown;
  constructor(message: string, code: string, status?: number, details?: unknown) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

function unwrapError(e: unknown): never {
  const ax = e as AxiosError<ApiError>;
  if (ax.response?.data && 'error' in ax.response.data) {
    const { code, message, details } = ax.response.data.error;
    throw new ApiRequestError(message, code, ax.response.status, details);
  }
  throw new ApiRequestError(
    ax.message || 'Network error',
    'NETWORK',
    ax.response?.status,
  );
}

export async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  try {
    const res = await http.get<ApiEnvelope<T>>(url, { params });
    return res.data.data;
  } catch (e) {
    unwrapError(e);
  }
}

export async function apiGetPaged<T>(
  url: string,
  params?: Record<string, unknown>,
): Promise<{ data: T; meta: PageMeta }> {
  try {
    const res = await http.get<ApiEnvelope<T>>(url, { params });
    return { data: res.data.data, meta: res.data.meta as PageMeta };
  } catch (e) {
    unwrapError(e);
  }
}

export async function apiSend<T>(
  method: 'post' | 'put' | 'patch' | 'delete',
  url: string,
  body?: unknown,
  config?: { params?: Record<string, unknown> },
): Promise<T> {
  try {
    const res = await http.request<ApiEnvelope<T>>({ method, url, data: body, params: config?.params });
    return res.data.data;
  } catch (e) {
    unwrapError(e);
  }
}

/** Multipart upload (images, files). Axios sets the boundary from the FormData. */
export async function apiUpload<T>(
  url: string,
  form: FormData,
  method: 'post' | 'patch' = 'post',
): Promise<T> {
  try {
    const res = await http.request<ApiEnvelope<T>>({ method, url, data: form });
    return res.data.data;
  } catch (e) {
    unwrapError(e);
  }
}

/** Trigger a browser download for a binary endpoint (reports). */
export async function apiDownload(url: string, params: Record<string, unknown>, fallbackName: string) {
  const res = await http.get(url, { params, responseType: 'blob' });
  const disp = String(res.headers['content-disposition'] || '');
  const match = disp.match(/filename="?([^"]+)"?/);
  const name = match?.[1] || fallbackName;
  const blobUrl = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
}

export { baseURL as apiBaseURL };
